import React, { memo, useMemo } from 'react'
import moment from 'moment'
import { Checkbox } from 'transfix-ui/components/Checkbox'
import { Spinner } from 'transfix-ui/components/Spinner'
import { Table, TableBody, TableCell, TableHead, TableRow } from 'transfix-ui/components/Table'
import { Box } from 'transfix-ui/core'
import { makeStyles } from 'transfix-ui/core/styles'
import { IOutboundRawEdiFile, IEdiMetadata } from 'types/graphqlTypes'
import formatCamelToTitleCase from 'utils/camelToTitleCase'
import { formatSnakeCaseStringToTitleCase } from 'utils/formatSnakeCaseStringToTitleCase'

type TPendingFile = Pick<
  IOutboundRawEdiFile,
  | 'id'
  | 'outboundConnectionConfigId'
  | 'shipmentId'
  | 'ediMessageType'
  | 'rogersFileDetailUrl'
  | 'createdAt'
  | 'ediMetadata'
>

interface IShipmentPendingFilesTableProps {
  loading: boolean
  pendingFiles: TPendingFile[]
  selectedFiles: string[]
  checkBoxChangeHandler: (outboundFileId: string, isChecked: boolean) => void
}

interface IRowCheckboxProps {
  outboundFileId: string
  disabled: boolean
  isChecked: boolean
  onChange: (outboundFileid: string, isChecked: boolean) => void
}

const useShipmentPendingFilesTableStyles = makeStyles(theme => ({
  table: {
    marginTop: theme.spacing(2),
  },
}))

const RowCheckbox = ({ disabled, isChecked, onChange, outboundFileId }: IRowCheckboxProps) => {
  const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(outboundFileId, event.target.checked)
  }

  return (
    <Checkbox
      disabled={disabled}
      checked={isChecked}
      onChange={changeHandler}
      dataTest={`test-checkbox-${outboundFileId}`}
    />
  )
}

interface IFormattedMetadataProps {
  value: string
  keyName: string
}

const FormattedMetadata = memo<IFormattedMetadataProps>(({ keyName, value }) => {
  const formatMetadataValue = (metadataField: string): string => {
    return formatSnakeCaseStringToTitleCase(metadataField)
  }

  const formatMetadataKey = (key: string): string => {
    return formatCamelToTitleCase(key)
  }

  const formattedKey = formatMetadataKey(keyName)
  const formattedValue = formatMetadataValue(value)
  return (
    <div>
      {formattedKey}: {formattedValue}
    </div>
  )
})

interface IMetadataListProps {
  metadata: IEdiMetadata
}

const ALLOWED_KEYS: string[] = ['subtype', 'shipperStopSequence', 'transactionSetSubtype']

const getVisibleFields = (metadata: IEdiMetadata): [string, string | number | null][] => {
  return Object.entries(metadata).filter(([key, value]) => {
    return value && ALLOWED_KEYS.includes(key)
  })
}

const MetadataList = ({ metadata }: IMetadataListProps) => {
  const visibleFields = useMemo(() => {
    return getVisibleFields(metadata)
  }, [metadata])

  if (!visibleFields.length) {
    return <>None</>
  }

  return (
    <>
      {visibleFields.map(([key, value]) => {
        return <FormattedMetadata keyName={key} value={value!.toString()} />
      })}
    </>
  )
}

export const ShipmentPendingFilesTable = ({
  checkBoxChangeHandler,
  loading,
  pendingFiles,
  selectedFiles,
}: IShipmentPendingFilesTableProps) => {
  const classes = useShipmentPendingFilesTableStyles()

  // 5/30/2023 9:35:03 AM
  const momentFormat = 'l LTS'

  return (
    <Table className={classes.table}>
      <TableHead>
        <TableRow tableCellType='th'>
          <TableCell>Select</TableCell>
          <TableCell>Outbound Config ID</TableCell>
          <TableCell>Shipment ID</TableCell>
          <TableCell>Message Type</TableCell>
          <TableCell>Link</TableCell>
          <TableCell>Created At</TableCell>
          <TableCell>Metadata</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading && (
          <TableRow>
            <TableCell className='tc' colSpan={7}>
              <Box>Loading </Box>
              <Spinner data-test='spinner' position='relative' />
            </TableCell>
          </TableRow>
        )}
        {!loading &&
          pendingFiles.length &&
          pendingFiles.map(file => (
            <TableRow key={file.id}>
              <TableCell>
                <RowCheckbox
                  outboundFileId={file.id}
                  disabled={false}
                  isChecked={selectedFiles.includes(file.id)}
                  onChange={checkBoxChangeHandler}
                />
              </TableCell>
              <TableCell>{file.outboundConnectionConfigId}</TableCell>
              <TableCell>{file.shipmentId}</TableCell>
              <TableCell>{file.ediMessageType}</TableCell>
              <TableCell>
                <a rel='noopener noreferrer' target='_blank' href={file.rogersFileDetailUrl}>
                  {file.id}
                </a>
              </TableCell>
              <TableCell>{moment(file.createdAt).format(momentFormat)}</TableCell>
              <TableCell>
                <MetadataList metadata={file.ediMetadata ?? {}} />
              </TableCell>
            </TableRow>
          ))}
        {!loading && !pendingFiles.length && (
          <TableRow>
            <TableCell className='tc' colSpan={7}>
              <Box>No Pending Files Reported</Box>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}
